import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Check,
  Download,
  Loader2,
  Minus,
  ShieldCheck,
  Stamp,
} from "lucide-react";
import {
  WeltfreigabeContent,
  BLOCK_LABEL,
  BLOCK_ORDER,
  recurrenceLabel,
} from "./nachweis/WeltfreigabeContent";

function formatDateTime(iso) {
  if (!iso) return "–";
  const d = new Date(iso);
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(iso) {
  if (!iso) return "–";
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export default function NachweisView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [nachweis, setNachweis] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.getNachweis(id).then(setNachweis);
  }, [id]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      await api.downloadNachweisPdf(id);
    } catch {
      toast.error("PDF konnte nicht geladen werden. Bitte erneut versuchen.");
    } finally {
      setDownloading(false);
    }
  };

  if (!nachweis) {
    return (
      <div className="max-w-3xl mx-auto p-10 text-slate-500">Lade…</div>
    );
  }

  const isWeltfreigabe = nachweis.nachweis_type === "weltfreigabe";

  return (
    <div
      className="max-w-4xl mx-auto space-y-6"
      data-testid={isWeltfreigabe ? "weltfreigabe-view" : "nachweis-view"}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap print:hidden">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-slate-700"
          data-testid="nachweis-back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        <Button
          variant="outline"
          onClick={handleDownloadPdf}
          disabled={downloading}
          data-testid="nachweis-download-pdf"
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          PDF herunterladen
        </Button>
      </div>

      {isWeltfreigabe ? (
        <WeltfreigabeNachweis nachweis={nachweis} />
      ) : (
        <UnterweisungNachweis nachweis={nachweis} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Unterweisungs-Nachweis (Mitarbeiter hat verstanden)
// ---------------------------------------------------------------------------

function UnterweisungNachweis({ nachweis }) {
  return (
    <article
      className="font-pdf bg-white border border-slate-200 shadow-sm rounded-md p-10 sm:p-14 nachweis-paper print:border-0 print:shadow-none print:p-0"
      data-testid="nachweis-document"
    >
      <header className="flex items-start justify-between border-b border-slate-300 pb-6 gap-4">
        <div className="min-w-0">
          <div
            className="text-xs uppercase tracking-[0.2em] text-slate-500"
            data-testid="nachweis-doctype-label"
          >
            Nachweis · Unterweisung
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-2 break-words">
            {nachweis.course_title}
          </h1>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Dokumentnummer
          </div>
          <div className="font-mono text-slate-900 font-semibold mt-1">
            {nachweis.document_number}
          </div>
        </div>
      </header>

      <CompanyBlock nachweis={nachweis} />

      <section className="mt-6 grid sm:grid-cols-2 gap-y-4 gap-x-10">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Mitarbeiter
          </div>
          <div
            className="text-slate-900 font-semibold mt-1"
            data-testid="nachweis-employee"
          >
            {nachweis.employee_name}
            {nachweis.employee_role && (
              <span className="text-sm font-normal text-slate-500 ml-2">
                · {nachweis.employee_role}
              </span>
            )}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Bestätigt am
          </div>
          <div className="text-slate-900 font-semibold mt-1">
            {formatDateTime(nachweis.completed_at)}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Version der Unterweisung
          </div>
          <div className="text-slate-900 font-semibold mt-1">
            {nachweis.course_version}
          </div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
          Beantwortete Verständnisfragen
        </h2>
        <ol className="mt-5 space-y-5">
          {nachweis.answers.map((a, idx) => (
            <li
              key={a.question_id}
              className="flex gap-4"
              data-testid={`nachweis-answer-${idx}`}
            >
              <div className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center shrink-0 mt-0.5 font-sans">
                {idx + 1}
              </div>
              <div className="flex-1">
                <p className="text-slate-900 font-semibold">{a.question_text}</p>
                <p className="text-slate-700 mt-1 flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-700 shrink-0" />
                  {a.given_text}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-12 pt-8 border-t border-slate-300">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full border-2 border-slate-900 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-6 h-6 text-slate-900" />
          </div>
          <div className="text-sm text-slate-700 leading-relaxed">
            <p className="font-semibold text-slate-900">
              Digitaler Bestätigungsvermerk
            </p>
            <p className="mt-1">
              Der/die oben genannte Mitarbeiter/in hat die Inhalte der
              Unterweisung {"\u201E"}{nachweis.course_title}{"\u201C"} in
              Version {nachweis.course_version} zur Kenntnis genommen und die
              Verständnisfragen am {formatDateTime(nachweis.completed_at)}{" "}
              korrekt beantwortet.
            </p>
            <p
              className="mt-3 text-xs text-slate-500 font-mono"
              data-testid="nachweis-id"
            >
              Nachweis-ID: {nachweis.id}
            </p>
          </div>
        </div>
      </section>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Weltfreigabe-Nachweis (Unternehmer hat Welt als Vorlage freigegeben)
// ---------------------------------------------------------------------------

const BLOCK_EXTRA = {
  questions: (snap) => (snap.questions_count ? ` (${snap.questions_count})` : ""),
  recurrence: (snap) => (snap.recurrence ? ` (${recurrenceLabel(snap.recurrence)})` : ""),
};

function WeltfreigabeNachweis({ nachweis }) {
  const snap = nachweis.world_snapshot;

  // Sprint C+++ (Feb 2026) – kein stilles Default-Rendering. Wenn der
  // Snapshot fehlt, ist das ein Daten-Konsistenz-Problem, das sichtbar
  // werden MUSS. Backend-Validator (Nachweis._validate_type_specific_fields)
  // sollte das schon abfangen — diese UI-Schicht ist die zweite Linie.
  if (!snap || !snap.included_blocks) {
    return (
      <article
        className="bg-rose-50 border border-rose-200 rounded-md p-8"
        data-testid="weltfreigabe-snapshot-missing"
      >
        <h2 className="font-heading text-lg font-semibold text-rose-900">
          Snapshot dieser Welt-Freigabe nicht verfügbar
        </h2>
        <p className="text-sm text-rose-800 mt-2">
          Für diesen Nachweis konnte der Welt-Stand zum Freigabe-Zeitpunkt
          nicht ermittelt werden. Bitte das Backend prüfen – ein
          Weltfreigabe-Nachweis muss laut Architektur immer einen
          vollständigen Snapshot enthalten.
        </p>
        <p
          className="mt-4 text-xs text-rose-700 font-mono"
          data-testid="nachweis-id"
        >
          Nachweis-ID: {nachweis.id}
        </p>
      </article>
    );
  }

  const blocks = snap.included_blocks;
  const reconstructed = snap.snapshot_reconstructed === true;
  // Sprint C+++ – Identitäts-Trio bevorzugt aus Snapshot (eingefroren).
  const releasedByName =
    snap.released_by_name || nachweis.released_by || "Unternehmer";
  const releasedAt = snap.released_at || nachweis.completed_at;
  const courseVersion = snap.course_version || nachweis.course_version;

  return (
    <article
      className="font-pdf bg-white border border-slate-200 shadow-sm rounded-md p-10 sm:p-14 nachweis-paper print:border-0 print:shadow-none print:p-0"
      data-testid="weltfreigabe-document"
    >
      <header className="flex items-start justify-between border-b border-slate-300 pb-6 gap-4">
        <div className="min-w-0">
          <div
            className="text-xs uppercase tracking-[0.2em] text-slate-500"
            data-testid="weltfreigabe-doctype-label"
          >
            Nachweis · Dokumentationswelt-Freigabe
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-2 break-words">
            {nachweis.course_title}
          </h1>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Dokumentnummer
          </div>
          <div className="font-mono text-slate-900 font-semibold mt-1">
            {nachweis.document_number}
          </div>
        </div>
      </header>

      <CompanyBlock nachweis={nachweis} />

      {/* Metadaten-Block */}
      <section
        className="mt-6 grid sm:grid-cols-2 gap-y-4 gap-x-10"
        data-testid="weltfreigabe-metadata"
      >
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Freigegeben durch
          </div>
          <div
            className="text-slate-900 font-semibold mt-1"
            data-testid="weltfreigabe-released-by"
          >
            {releasedByName}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Freigegeben am
          </div>
          <div
            className="text-slate-900 font-semibold mt-1"
            data-testid="weltfreigabe-released-at"
          >
            {formatDateTime(releasedAt)}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">
            Version der Dokumentationswelt
          </div>
          <div
            className="text-slate-900 font-semibold mt-1 font-mono"
            data-testid="weltfreigabe-version"
          >
            v{courseVersion}
          </div>
        </div>
      </section>

      {/* Migration-Hinweis (dezent, nur bei rekonstruiertem Snapshot) */}
      {reconstructed && (
        <div
          className="mt-6 text-xs text-slate-500 italic border-l-2 border-slate-200 pl-3 py-1"
          data-testid="weltfreigabe-reconstructed-note"
        >
          Hinweis: Der Welt-Stand zum Zeitpunkt dieser Freigabe wurde am{" "}
          {formatDate(snap.snapshot_reconstructed_at)} aus dem aktuellen
          Datenbestand rekonstruiert.
        </div>
      )}

      {/* Bausteine-Liste */}
      <section className="mt-10">
        <h2 className="font-heading text-lg font-semibold text-slate-900 border-b border-slate-200 pb-2">
          Enthaltene Bausteine dieser Dokumentationswelt
        </h2>
        <ul
          className="mt-5 space-y-2"
          data-testid="weltfreigabe-blocks"
        >
          {BLOCK_ORDER.map((key) => {
            const included = !!blocks[key];
            const label = BLOCK_LABEL[key];
            const extra = included && BLOCK_EXTRA[key] ? BLOCK_EXTRA[key](snap) : "";
            return (
              <li
                key={key}
                className="flex items-start gap-3"
                data-testid={`weltfreigabe-block-${key}`}
              >
                <div
                  className={`w-5 h-5 rounded shrink-0 mt-0.5 flex items-center justify-center ${
                    included
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-slate-100 text-slate-400"
                  }`}
                >
                  {included ? (
                    <Check className="w-3.5 h-3.5" />
                  ) : (
                    <Minus className="w-3.5 h-3.5" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span
                    className={`text-sm ${
                      included ? "text-slate-900 font-medium" : "text-slate-500"
                    }`}
                  >
                    {label}
                    {extra}
                  </span>
                  {!included && (
                    <span className="text-xs text-slate-400 ml-2">
                      (nicht enthalten)
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Sprint C+++ Plus (Feb 2026) – Vollständige Prüfakte
          Anzeige der echten Bausteine-Inhalte aus snap.blocks. Ausschließlich
          Snapshot-Daten, KEINE Live-Abfragen. Print-fähig. */}
      <WeltfreigabeContent snap={snap} />

      {/* Bestätigungsvermerk */}
      <section className="mt-12 pt-8 border-t border-slate-300">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full border-2 border-slate-900 flex items-center justify-center shrink-0">
            <Stamp className="w-6 h-6 text-slate-900" />
          </div>
          <div className="text-sm text-slate-700 leading-relaxed">
            <p className="font-semibold text-slate-900">
              Bestätigungsvermerk
            </p>
            <p className="mt-1" data-testid="weltfreigabe-confirmation-text">
              Die oben genannte Dokumentationswelt {"\u201E"}
              {nachweis.course_title}
              {"\u201C"} wurde am {formatDateTime(releasedAt)} durch{" "}
              {releasedByName} als betriebliche Vorlage freigegeben. Die
              Freigabe umfasst die zu diesem Zeitpunkt enthaltenen Bausteine
              der Dokumentationswelt in Version {courseVersion}.
            </p>
            <p
              className="mt-3 text-xs text-slate-500 font-mono"
              data-testid="nachweis-id"
            >
              Nachweis-ID: {nachweis.id}
            </p>
          </div>
        </div>
      </section>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

function CompanyBlock({ nachweis }) {
  return (
    <section
      className="mt-8 pb-6 border-b border-slate-200"
      data-testid="nachweis-company-block"
    >
      <div className="text-xs uppercase tracking-widest text-slate-500">
        Betrieb
      </div>
      <div className="text-slate-900 font-semibold mt-1">
        {nachweis.company_name || "–"}
      </div>
      {nachweis.company_address && (
        <div className="text-sm text-slate-700 mt-0.5">
          {nachweis.company_address}
        </div>
      )}
      <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-slate-700">
        {nachweis.company_contact && (
          <div>
            <span className="text-slate-500">Verantwortliche Person: </span>
            {nachweis.company_contact}
          </div>
        )}
        {nachweis.company_bg_name && (
          <div data-testid="nachweis-bg">
            <span className="text-slate-500">BG: </span>
            {nachweis.company_bg_name}
            {nachweis.company_bg_id ? ` · Nr. ${nachweis.company_bg_id}` : ""}
          </div>
        )}
      </div>
    </section>
  );
}
